import { useState, useEffect } from "react";
import SEO from "../components/SEO";


// ---------- COLOR TOKENS (matches site-wide black/white/gray palette) ----------
const C = {
  black: "#343F86",
  charcoal: "#343F86",
  graphite: "#262E63",
  steel: "#3A4060",
  ash: "#5B6072",
  silver: "#8A8F9C",
  smoke: "#CDD1E0",
  fog: "#E1E3EE",
  pearl: "#ECEEF6",
  white: "#F6F7FC",
  pure: "#FFFFFF",
};

const fontFamily = "'Inter', 'Montserrat', sans-serif";

// ---------- DATA ----------
const VALUES = [
  {
    title: "We build what we design",
    body: "Design and construction sit under one roof here. The person who drew the ICU layout is on a call with the site team when it's actually being built.",
  },
  {
    title: "Every drawing becomes a room someone recovers in",
    body: "It's easy to lose sight of that across a 400-page spec set. We don't. The tolerance on a door swing matters when it's a stretcher going through it.",
  },
  {
    title: "Compliance isn't paperwork",
    body: "NABH checklists and infection-control zoning exist because someone worked out, the hard way, what protects patients. We treat the standard as the floor, not a hoop.",
  },
  {
    title: "Site time is part of the job",
    body: "Designers here visit live sites. Engineers here read architectural drawings. Nobody stays only at a desk for the life of a project.",
  },
];

const BENEFITS = [
  { icon: "health", label: "Health cover", body: "Comprehensive medical insurance for you and your family." },
  { icon: "sites", label: "Multi-city exposure", body: "Work across active sites in Hyderabad, Vijayawada, Vizag, and beyond." },
  { icon: "cert", label: "Certification support", body: "Funded NABH, biomedical, and MEP certification programs." },
  { icon: "relocate", label: "Relocation support", body: "Assistance for site postings outside your home city." },
  { icon: "access", label: "Direct access to leads", body: "Flat reporting lines to design and engineering leads, not layers of hierarchy." },
  { icon: "bonus", label: "Milestone bonus", body: "Performance bonus tied to project handover, not just tenure." },
];

// ---------- ICONS ----------
function BenefitIcon({ icon, color, size = 24 }) {
  const p = { fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    health: (
      <>
        <path d="M12 21s-7-4.35-9.5-8.8C.8 8.7 2.4 5 6 5c2 0 3.4 1.2 4 2.4C10.6 6.2 12 5 14 5c3.6 0 5.2 3.7 3.5 7.2C19 16.65 12 21 12 21Z" {...p} />
      </>
    ),
    sites: (
      <>
        <circle cx="6" cy="8" r="2.4" {...p} />
        <circle cx="18" cy="6" r="2.4" {...p} />
        <circle cx="12" cy="18" r="2.4" {...p} />
        <path d="M8 9.3l3 6.8M16 7.3l-3 6.8M8.4 7.2l7.2-.6" {...p} />
      </>
    ),
    cert: (
      <>
        <circle cx="12" cy="9" r="5" {...p} />
        <path d="M8.5 13.5L7 21l5-2.5 5 2.5-1.5-7.5" {...p} />
      </>
    ),
    relocate: (
      <>
        <path d="M4 20V10l8-6 8 6v10" {...p} />
        <path d="M9 20v-6h6v6" {...p} />
        <path d="M15 3.5l4 3" {...p} />
      </>
    ),
    access: (
      <>
        <circle cx="6" cy="12" r="2.2" {...p} />
        <circle cx="18" cy="6" r="2.2" {...p} />
        <circle cx="18" cy="18" r="2.2" {...p} />
        <path d="M8 11l8-4M8 13l8 4" {...p} />
      </>
    ),
    bonus: (
      <>
        <circle cx="12" cy="12" r="8" {...p} />
        <path d="M12 7v5l3.5 2" {...p} />
      </>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      {icons[icon]}
    </svg>
  );
}

// ---------- APP ----------
export default function Careers() {
  const [isMobile, setIsMobile] = useState(false);
  const [showApply, setShowApply] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showApply ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showApply]);

  return (
     <>
    <SEO/>
    <div className="careers-page">
      <style>{`
        .careers-page {
          min-height: 100vh;
          background: ${C.pure};
          color: ${C.black};
          font-family: ${fontFamily};
          padding-top: 68px;
        }
        @media (max-width: 900px) { .careers-page { padding-top: 60px; } }
        @media (max-width: 480px) { .careers-page { padding-top: 56px; } }
      `}</style>

      {/* ===== HERO (dark, matches site-wide hero style) ===== */}
      <section
        style={{
          background: C.black,
          padding: isMobile ? "56px 20px" : "96px 48px 56px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute", inset: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto" }}>
          <p
            style={{
              fontFamily: fontFamily,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "3px",
              color: C.smoke,
              textTransform: "uppercase",
              background: "rgba(255,255,255,0.08)",
              display: "inline-block",
              padding: "6px 14px",
              borderRadius: 4,
              margin: isMobile ? "0 0 16px" : "0 0 24px",
            }}
          >
            Careers · PR Hospital Designers Hospital Infrastructure
          </p>
          <h1
            style={{
              fontFamily: fontFamily,
              fontWeight: 700,
              fontSize: isMobile ? 32 : "clamp(32px, 5vw, 56px)",
              margin: isMobile ? "0 0 16px" : "0 0 24px",
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
              color: C.pure,
            }}
          >
            Build the rooms where lives are saved.
          </h1>
          <p style={{ fontSize: isMobile ? 15 : 16, color: C.smoke, lineHeight: 1.8, margin: isMobile ? "0 0 24px" : "0 0 32px" }}>
            We design and build operation theatres, ICUs, medical gas systems, and hospital
            interiors across Telangana and Andhra Pradesh. Every drawing here eventually becomes
            a room a patient, a nurse, or a surgeon actually stands in.
          </p>
          <button
            onClick={() => setShowApply(true)}
            style={{
              display: "inline-block",
              fontFamily: fontFamily,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: C.black,
              background: C.pure,
              padding: "13px 28px",
              borderRadius: 10,
              border: "none",
              cursor: "pointer",
            }}
          >
            Send Us Your Profile
          </button>
        </div>
      </section>

      {/* ===== VALUES ===== */}
      <section style={{ padding: isMobile ? "40px 20px" : "72px 48px", background: C.pure }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: isMobile ? "left" : "center", marginBottom: isMobile ? 28 : 48 }}>
            <div style={{ fontFamily: fontFamily, fontSize: 11, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: C.ash, marginBottom: 12 }}>
              How We Work
            </div>
            <h2 style={{ fontFamily: fontFamily, fontSize: isMobile ? 26 : "clamp(26px,3.5vw,40px)", fontWeight: 700, color: C.black, margin: 0 }}>
              What Drives the Team
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))", gap: isMobile ? 14 : 20 }}>
            {VALUES.map((v) => (
              <div key={v.title} style={{ background: C.pearl, border: `1px solid ${C.fog}`, borderRadius: 12, padding: isMobile ? "22px 20px" : "26px 24px" }}>
                <h3 style={{ fontFamily: fontFamily, fontSize: isMobile ? 18 : 20, fontWeight: 600, margin: "0 0 12px", color: C.black }}>{v.title}</h3>
                <p style={{ fontSize: 13, color: C.steel, lineHeight: 1.7, margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== NO OPEN ROLES ===== */}
      <section id="open-roles" style={{ padding: isMobile ? "0 20px 40px" : "0 48px 72px", background: C.pure }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div
            style={{
              border: `1px dashed ${C.fog}`,
              borderRadius: 14,
              background: C.pearl,
              padding: isMobile ? "40px 24px" : "64px 48px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 16 }}>🏗️</div>
            <h2 style={{ fontFamily: fontFamily, fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.black, margin: "0 0 12px" }}>
              No Open Positions Right Now
            </h2>
            <p style={{ fontSize: isMobile ? 14 : 15, color: C.steel, lineHeight: 1.75, maxWidth: 480, margin: "0 auto 28px" }}>
              We don't have any active openings at the moment, but we're always building our
              network of architects, engineers, and site professionals for upcoming projects.
            </p>
            <button
              onClick={() => setShowApply(true)}
              style={{
                fontFamily: fontFamily,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: C.pure,
                background: C.black,
                padding: "13px 28px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                width: isMobile ? "100%" : "auto",
              }}
            >
              Submit Your Profile Anyway
            </button>
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section style={{ padding: isMobile ? "0 20px 48px" : "0 48px 72px", background: C.pure }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: isMobile ? "left" : "center", marginBottom: isMobile ? 24 : 40 }}>
            <div style={{ fontFamily: fontFamily, fontSize: 11, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: C.ash, marginBottom: 12 }}>
              What You Get
            </div>
            <h2 style={{ fontFamily: fontFamily, fontSize: isMobile ? 26 : "clamp(26px,3.5vw,40px)", fontWeight: 700, color: C.black, margin: 0 }}>
              Working at PR Hospital Designers
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: isMobile ? 12 : 18 }}>
            {BENEFITS.map((b) => (
              <div key={b.label} style={{ border: `1px solid ${C.fog}`, background: C.pearl, borderRadius: 12, padding: isMobile ? "18px 18px" : "22px 22px 24px" }}>
                <BenefitIcon icon={b.icon} color={C.black} />
                <div style={{ fontFamily: fontFamily, fontSize: isMobile ? 15 : 16, fontWeight: 600, margin: "14px 0 6px", color: C.black }}>{b.label}</div>
                <p style={{ fontSize: 13, color: C.steel, lineHeight: 1.6, margin: 0 }}>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOOTER (visually distinct from body — off-white with top border) ===== */}
     

      {showApply && <ApplyModal onClose={() => setShowApply(false)} isMobile={isMobile} />}
    </div>
    </>
  );
}

function ApplyModal({ onClose, isMobile }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [resume, setResume] = useState(null);
  const [sent, setSent] = useState(false);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = name.trim().length > 0 && emailValid;

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) {
      setResume(null);
      return;
    }
    const okType = /\.(pdf|doc|docx)$/i.test(file.name);
    const okSize = file.size <= 10 * 1024 * 1024; // 10MB
    if (!okType) {
      alert("Please choose a PDF, DOC, or DOCX file.");
      e.target.value = "";
      setResume(null);
      return;
    }
    if (!okSize) {
      alert("File is larger than 10MB — please choose a smaller file.");
      e.target.value = "";
      setResume(null);
      return;
    }
    setResume(file);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    const subject = `General Application — PR Hospital Designers Careers`;
    const body =
      `Name: ${name}\n` +
      `Email: ${email}\n` +
      `Resume file: ${resume ? resume.name : "(not attached — remember to attach it below)"}\n\n` +
      `Message:\n${message || "(no message provided)"}`;

    const mailtoLink = `mailto:careers@tevah.in?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    setSent(true);
    setTimeout(() => {
      window.location.href = mailtoLink;
    }, 600);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(5px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 2000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          maxHeight: "90vh",
          overflowY: "auto",
          background: C.pure,
          border: `1px solid ${C.fog}`,
          borderRadius: 16,
          padding: isMobile ? 22 : 28,
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: C.pearl,
            border: "none",
            color: C.black,
            width: 32,
            height: 32,
            borderRadius: "50%",
            fontSize: 16,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ✕
        </button>

        {sent ? (
          <div style={{ padding: "30px 0", textAlign: "center" }}>
            <p style={{ fontFamily: fontFamily, fontSize: 19, fontWeight: 700, margin: "0 0 10px", color: C.black }}>
              Opening your email app…
            </p>
            <p style={{ fontSize: 13.5, color: C.steel, margin: "0 0 16px", lineHeight: 1.6 }}>
              Your details are filled in — just hit send from there to careers@tevah.in.
            </p>
            {resume && (
              <div
                style={{
                  border: `1px dashed ${C.fog}`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  fontFamily: fontFamily,
                  fontSize: 12,
                  color: C.steel,
                  textAlign: "left",
                }}
              >
                ⚠ Don't forget: attach <b style={{ color: C.black }}>{resume.name}</b> to the email before sending —
                your browser can't attach it for you.
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p style={{ fontFamily: fontFamily, fontSize: 11, color: C.ash, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 600, margin: "0 0 6px" }}>
              General Application
            </p>
            <h3 style={{ fontFamily: fontFamily, fontSize: 21, fontWeight: 700, margin: "0 0 20px", color: C.black }}>
              Submit Your Profile
            </h3>

            <Field label="Full name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                style={inputStyle}
              />
            </Field>

            <Field label="Email address">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
              />
            </Field>

            <Field label="Message (optional)">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A line about your background, area of expertise, or a link to your portfolio/resume"
                rows={4}
                style={{ ...inputStyle, resize: "vertical" }}
              />
            </Field>

            <Field label="Resume (PDF, DOC, or DOCX)">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ ...inputStyle, padding: "8px 10px", fontSize: 13 }}
              />
              {resume && (
                <span style={{ display: "block", fontFamily: fontFamily, fontSize: 11, color: C.ash, marginTop: 6 }}>
                  Selected: {resume.name} ({(resume.size / 1024).toFixed(0)} KB)
                </span>
              )}
            </Field>

            {resume && (
              <div
                style={{
                  border: `1px dashed ${C.fog}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  marginBottom: 16,
                  fontFamily: fontFamily,
                  fontSize: 11,
                  color: C.steel,
                  lineHeight: 1.5,
                }}
              >
                Heads up: email links can't attach files automatically. Once your email
                app opens, you'll need to manually attach <b style={{ color: C.black }}>{resume.name}</b> before hitting send.
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                width: "100%",
                marginTop: 8,
                fontFamily: fontFamily,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: canSubmit ? C.pure : C.silver,
                background: canSubmit ? C.black : C.fog,
                border: "none",
                borderRadius: 10,
                padding: "14px 20px",
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
            >
              Send Application
            </button>
            <p style={{ fontSize: 11, color: C.silver, marginTop: 12, textAlign: "center", lineHeight: 1.5 }}>
              This opens your email app with the message pre-filled to careers@tevah.in.
            </p>
          </form>
        )}
      </div>
    </div>

  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <span style={{ display: "block", fontFamily: fontFamily, fontSize: 11, color: C.ash, letterSpacing: "0.5px", textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  background: "#F6F7FC",
  border: `1px solid ${C.fog}`,
  borderRadius: 10,
  color: C.black,
  fontFamily: fontFamily,
  fontSize: 14,
  padding: "11px 14px",
  outline: "none",
};